import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tab } from '@headlessui/react';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  Eye,
  Loader2,
  Lock,
  LogOut,
  Search,
  Target,
} from 'lucide-react';
import { storage } from '../../Shared/Utils/authStorage';
import { logoutUser } from '../../Shared/Services/ApiClient';
import { getCandidateById } from '../Services/CandidateService';
import {
  changeCandidatePassword,
  getCandidateSettings,
  updateCandidateSettings,
} from '../Services/CandidateSettingsService';
import {
  CandidateSettings,
  CandidateSettingsUpdate,
  JOB_ALERT_FREQUENCY_OPTIONS,
  OPEN_TO_WORK_OPTIONS,
  OpenToWorkStatus,
} from '../Types/CandidateSettingsType';
import { CandidateProfile } from '../Types/CandidateType';
import { getPasswordValidationError } from '../../Shared/Utils/validationPolicy';
import { useValidationPolicy } from '../../Shared/Hooks/useValidationPolicy';
import { PasswordPolicyHints } from '../../Shared/Component/forms/PasswordPolicyHints';

type TabId = 'alerts' | 'visibility' | 'open-to-work' | 'security';

interface Toast {
  variant: 'success' | 'error';
  title: string;
  message: string;
}

const inputClass =
  'w-full rounded-xl border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-slate-100 placeholder-slate-500 outline-none transition-all focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-500/20';

function SectionCard({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-xl backdrop-blur-md sm:p-7">
      <header className="mb-5 flex items-start gap-3">
        <div className="mt-0.5 rounded-xl bg-cyan-500/10 p-2.5 text-cyan-400 ring-1 ring-cyan-500/20">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-white">{title}</h2>
          <p className="mt-0.5 text-xs text-slate-400">{description}</p>
        </div>
      </header>
      {children}
    </section>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-4 rounded-xl border border-white/5 bg-slate-950/40 p-4 transition-colors ${
        disabled ? 'opacity-50' : 'hover:border-cyan-500/20'
      }`}
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-200">{label}</p>
        <p className="mt-0.5 text-xs text-slate-500">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-200 disabled:cursor-not-allowed ${
          checked ? 'bg-cyan-500' : 'bg-slate-700'
        }`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
            checked ? 'translate-x-[22px]' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}

export default function CandidateSettingsDashboard(): React.JSX.Element {
  const userId = storage.getUserId();

  const [activeTab, setActiveTab] = useState<TabId>('alerts');
  const [settings, setSettings] = useState<CandidateSettings | null>(null);
  const [baseline, setBaseline] = useState<CandidateSettings | null>(null);
  const [account, setAccount] = useState<CandidateProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  // ===== password form =====
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const [passwordNotice, setPasswordNotice] = useState<string>('');
  const { policy } = useValidationPolicy();

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: 'alerts', label: 'Job Alerts', icon: Bell },
    { id: 'visibility', label: 'Visibility', icon: Eye },
    { id: 'open-to-work', label: 'Open to Work', icon: Target },
    { id: 'security', label: 'Security', icon: Lock },
  ];

  const load = useCallback(async () => {
    if (!userId) {
      setLoadError('You need to be signed in to manage settings.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError('');

    const response = await getCandidateSettings(userId);

    if (response.success && response.data) {
      setSettings(response.data);
      setBaseline(response.data);
    } else {
      setLoadError(response.message || 'Could not load your settings.');
    }

    // Read-only account facts for the security tab. Non-fatal if it fails.
    const profileResponse = await getCandidateById(userId);
    if (profileResponse.success && profileResponse.data) {
      setAccount(profileResponse.data);
    }

    setLoading(false);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  /** Only the fields the user actually touched are sent. */
  const pendingChanges = useMemo<CandidateSettingsUpdate>(() => {
    if (!settings || !baseline) return {};
    const diff: Record<string, unknown> = {};
    (Object.keys(baseline) as (keyof CandidateSettings)[]).forEach((key) => {
      if (key === 'userId' || key === 'createdAt' || key === 'updatedAt') return;
      if (settings[key] !== baseline[key]) {
        diff[key] = settings[key];
      }
    });
    return diff as CandidateSettingsUpdate;
  }, [settings, baseline]);

  const dirtyCount = Object.keys(pendingChanges).length;

  const update = <K extends keyof CandidateSettings>(key: K, value: CandidateSettings[K]) => {
    setSettings((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async () => {
    if (!userId || !settings || saving || dirtyCount === 0) return;

    setSaving(true);
    const response = await updateCandidateSettings(userId, pendingChanges);

    if (response.success && response.data) {
      setSettings(response.data);
      setBaseline(response.data);
      setToast({
        variant: 'success',
        title: 'Settings saved',
        message: `${dirtyCount} change${dirtyCount === 1 ? '' : 's'} applied.`,
      });
    } else {
      setToast({
        variant: 'error',
        title: 'Could not save',
        message: response.message || 'Please try again.',
      });
    }

    setSaving(false);
  };

  const handleDiscard = () => {
    if (baseline) setSettings(baseline);
    setToast(null);
  };

  const handleChangePassword = async () => {
    if (!userId) return;

    setPasswordError('');
    setPasswordNotice('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Fill in all three fields.');
      return;
    }
    const policyError = getPasswordValidationError(newPassword, policy);
    if (policyError) {
      setPasswordError(policyError);
      return;
    }    if (newPassword !== confirmPassword) {
      setPasswordError('The new passwords do not match.');
      return;
    }

    setPasswordBusy(true);
    const response = await changeCandidatePassword(userId, { currentPassword, newPassword });

    if (response.success) {
      const revoked = response.data?.otherSessionsRevoked ?? 0;
      setPasswordNotice(
        `Password updated. ${revoked} active ${
          revoked === 1 ? 'session was' : 'sessions were'
        } signed out, including this one — please sign in again.`
      );
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPasswordError(response.message || 'Could not update your password.');
    }

    setPasswordBusy(false);
  };

  // ===== states =====
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950 text-slate-100">
        <div className="flex items-center gap-3 text-sm text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin text-cyan-400" />
          Loading your settings…
        </div>
      </div>
    );
  }

  if (loadError || !settings) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950 p-6 text-slate-100">
        <div className="max-w-md rounded-2xl border border-red-500/30 bg-red-950/30 p-8 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-red-400" />
          <h1 className="mt-4 text-lg font-bold text-white">Settings unavailable</h1>
          <p className="mt-2 text-sm text-slate-300">{loadError}</p>
          <button
            onClick={load}
            className="mt-6 rounded-xl bg-cyan-500 px-6 py-2.5 text-sm font-bold text-slate-950 transition-colors hover:bg-cyan-400"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950 px-4 py-8 pb-36 text-slate-100 antialiased sm:px-8">
      <div className="mx-auto w-full max-w-5xl">
        {/* Toast */}
        {toast && (
          <div className="fixed right-4 top-4 z-50 w-full max-w-sm animate-fade-in">
            <div
              className={`flex items-start gap-3 rounded-xl border p-4 shadow-2xl backdrop-blur-md ${
                toast.variant === 'success'
                  ? 'border-emerald-500/30 bg-emerald-950/80'
                  : 'border-red-500/30 bg-red-950/80'
              }`}
            >
              {toast.variant === 'success' ? (
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
              ) : (
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-white">{toast.title}</p>
                <p className="mt-0.5 text-xs text-slate-300">{toast.message}</p>
              </div>
              <button
                onClick={() => setToast(null)}
                className="text-slate-400 transition-colors hover:text-white"
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <header className="mb-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-cyan-400">
            <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-400" />
            Candidate Preferences
          </div>
          <h1 className="mt-2 bg-gradient-to-r from-cyan-400 via-pink-500 to-amber-400 bg-clip-text text-3xl font-black tracking-tight text-transparent sm:text-4xl">
            Settings
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Control how you are contacted and who can find you.
          </p>
        </header>

        <Tab.Group selectedIndex={tabs.findIndex((t) => t.id === activeTab)} onChange={(i) => setActiveTab(tabs[i].id)}>
          <Tab.List className="flex gap-2 overflow-x-auto rounded-2xl border border-white/10 bg-white/5 p-1.5 backdrop-blur-md">
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                className={({ selected }) =>
                  `flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                    selected
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-blue-900/30'
                      : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
                  }`
                }
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </Tab>
            ))}
          </Tab.List>

          <div className="mt-6 space-y-5">
            {/* ==================== JOB ALERTS ==================== */}
            <Tab.Panel as={motion.div} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
              <SectionCard
                icon={Bell}
                title="Matching job alerts"
                description="Get notified when a role matches your profile."
              >
                <div className="space-y-3">
                  <ToggleRow
                    label="Enable job alerts"
                    description="Master switch for every job alert below."
                    checked={settings.jobAlertEnabled}
                    onChange={(v) => update('jobAlertEnabled', v)}
                  />

                  <div className={`rounded-xl border border-white/5 bg-slate-950/40 p-4 ${settings.jobAlertEnabled ? '' : 'pointer-events-none opacity-50'}`}>
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      How often should we email you?
                    </p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      {JOB_ALERT_FREQUENCY_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => update('jobAlertFrequency', option.value)}
                          className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${
                            settings.jobAlertFrequency === option.value
                              ? 'border-cyan-400 bg-cyan-500/15 text-cyan-300'
                              : 'border-white/10 text-slate-400 hover:border-white/20 hover:text-slate-200'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <ToggleRow
                    label="New matching roles"
                    description="One email per role that matches your target roles."
                    checked={settings.emailJobAlerts}
                    onChange={(v) => update('emailJobAlerts', v)}
                    disabled={!settings.jobAlertEnabled}
                  />
                  <ToggleRow
                    label="Weekly digest"
                    description="A single Monday summary instead of individual emails."
                    checked={settings.emailWeeklyDigest}
                    onChange={(v) => update('emailWeeklyDigest', v)}
                    disabled={!settings.jobAlertEnabled}
                  />
                </div>
              </SectionCard>

              <SectionCard
                icon={Bell}
                title="Other emails"
                description="Transactional messages stay on regardless of job alerts."
              >
                <div className="space-y-3">
                  <ToggleRow
                    label="Recruiter outreach"
                    description="When a recruiter contacts you directly."
                    checked={settings.emailRecruiterOutreach}
                    onChange={(v) => update('emailRecruiterOutreach', v)}
                  />
                  <ToggleRow
                    label="Application updates"
                    description="Status changes on roles you have applied to."
                    checked={settings.emailApplicationUpdates}
                    onChange={(v) => update('emailApplicationUpdates', v)}
                  />
                  <ToggleRow
                    label="Product and community news"
                    description="Occasional updates about Nexgent. No marketing spam."
                    checked={settings.emailProductUpdates}
                    onChange={(v) => update('emailProductUpdates', v)}
                  />
                </div>
              </SectionCard>
            </Tab.Panel>

            {/* ==================== VISIBILITY ==================== */}
            <Tab.Panel as={motion.div} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
              <SectionCard
                icon={Eye}
                title="Who can see you"
                description="Turning off recruiter visibility hides your profile from search."
              >
                <div className="space-y-3">
                  <ToggleRow
                    label="Visible to recruiters"
                    description="Your profile appears in recruiter search results."
                    checked={settings.profileVisibleToRecruiters}
                    onChange={(v) => update('profileVisibleToRecruiters', v)}
                  />
                  <ToggleRow
                    label="Searchable by recruiters"
                    description="Allow keyword and title based matching."
                    checked={settings.searchableByRecruiters}
                    onChange={(v) => update('searchableByRecruiters', v)}
                    disabled={!settings.profileVisibleToRecruiters}
                  />
                  <ToggleRow
                    label="Leadership network directory"
                    description="Appear in the member directory for other leaders."
                    checked={settings.showInLeadershipNetwork}
                    onChange={(v) => update('showInLeadershipNetwork', v)}
                  />
                </div>
              </SectionCard>

              <SectionCard
                icon={Search}
                title="Contact details"
                description="Control what is shared once a recruiter can see you."
              >
                <div className="space-y-3">
                  <ToggleRow
                    label="Show my LinkedIn"
                    description="Display your LinkedIn URL on your public profile."
                    checked={settings.showLinkedin}
                    onChange={(v) => update('showLinkedin', v)}
                  />
                  <ToggleRow
                    label="Allow direct messages"
                    description="Recruiters can message you without an introduction."
                    checked={settings.allowDirectMessages}
                    onChange={(v) => update('allowDirectMessages', v)}
                    disabled={!settings.profileVisibleToRecruiters}
                  />
                </div>
              </SectionCard>
            </Tab.Panel>

            {/* ==================== OPEN TO WORK ==================== */}
            <Tab.Panel as={motion.div} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
              <SectionCard
                icon={Target}
                title="Looking for work"
                description="Sets how aggressively recruiters should approach you."
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {OPEN_TO_WORK_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => update('openToWorkStatus', option.value as OpenToWorkStatus)}
                      className={`rounded-xl border p-4 text-left transition-all ${
                        settings.openToWorkStatus === option.value
                          ? 'border-cyan-400 bg-cyan-500/15'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <p
                        className={`text-sm font-bold ${
                          settings.openToWorkStatus === option.value ? 'text-cyan-300' : 'text-slate-200'
                        }`}
                      >
                        {option.label}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">{option.hint}</p>
                    </button>
                  ))}
                </div>

                <div className="mt-4 space-y-3">
                  <ToggleRow
                    label="Show open-to-work status publicly"
                    description="Display the badge on your profile."
                    checked={settings.openToWorkVisible}
                    onChange={(v) => update('openToWorkVisible', v)}
                  />
                </div>
              </SectionCard>

              <SectionCard
                icon={Target}
                title="Target roles and availability"
                description="Used to match you against incoming roles."
              >
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Target roles
                  </label>
                  <input
                    type="text"
                    value={settings.targetRoles ?? ''}
                    onChange={(e) => update('targetRoles', e.target.value)}
                    placeholder="VP Engineering, Head of Platform, CTO"
                    className={`${inputClass} mt-2`}
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    Comma separated, up to 10 roles.
                  </p>
                </div>

                <div className="mt-5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Notice period: {settings.noticePeriodDays} days
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={180}
                    step={5}
                    value={settings.noticePeriodDays}
                    onChange={(e) => update('noticePeriodDays', Number(e.target.value))}
                    className="mt-3 w-full accent-cyan-500"
                  />
                  <div className="mt-1 flex justify-between text-[10px] text-slate-600">
                    <span>Available now</span>
                    <span>6 months</span>
                  </div>
                </div>
              </SectionCard>
            </Tab.Panel>

            {/* ==================== SECURITY ==================== */}
            <Tab.Panel as={motion.div} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
              <SectionCard
                icon={Lock}
                title="Change password"
                description="Changing your password signs you out everywhere, including this device."
              >
                <div className="max-w-md space-y-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Current password
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                      className={`${inputClass} mt-2`}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      New password
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      placeholder="At least 8 characters"
                      className={`${inputClass} mt-2`}
                    />
                    <PasswordPolicyHints password={newPassword} policy={policy} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Confirm new password
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      className={`${inputClass} mt-2`}
                    />
                  </div>

                  {passwordError && (
                    <p className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-950/40 p-3 text-xs text-red-300">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      {passwordError}
                    </p>
                  )}
                  {passwordNotice && (
                    <p className="flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-950/40 p-3 text-xs text-emerald-300">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                      {passwordNotice}
                    </p>
                  )}

                  <button
                    onClick={handleChangePassword}
                    disabled={passwordBusy}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 py-3 text-sm font-bold text-white shadow-lg shadow-blue-900/30 transition-all hover:shadow-blue-500/30 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {passwordBusy ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Updating…
                      </>
                    ) : (
                      'Update password'
                    )}
                  </button>
                </div>
              </SectionCard>

              <SectionCard
                icon={Search}
                title="Account"
                description="Read-only details for the account you are signed in with."
              >
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {[
                    { label: 'Email', value: account?.userEmail || '—' },
                    { label: 'Role', value: account?.userRole || '—' },
                    { label: 'Account status', value: account?.userStatus || '—' },
                    {
                      label: 'Email verified',
                      value: account?.isUserVerified ? 'Yes' : 'Not yet',
                    },
                    { label: 'Member since', value: account?.createdAt || '—' },
                  ].map((row) => (
                    <div key={row.label} className="rounded-xl border border-white/5 bg-slate-950/40 p-4">
                      <dt className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                        {row.label}
                      </dt>
                      <dd className="mt-1 truncate text-sm font-semibold text-slate-200">{row.value}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-5 flex justify-end">
                  <button
                    onClick={logoutUser}
                    className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-xs font-semibold text-slate-300 transition-colors hover:border-red-500/40 hover:text-red-300"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </div>
              </SectionCard>
            </Tab.Panel>
          </div>
        </Tab.Group>
      </div>

      {/* Save bar */}
      {dirtyCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-slate-950/90 p-4 shadow-[0_-12px_24px_rgba(0,0,0,0.5)] backdrop-blur-lg">
          <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-3 sm:flex-row sm:justify-between">
            <p className="text-xs font-semibold text-slate-400">
              {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
            </p>
            <div className="flex w-full gap-3 sm:w-auto">
              <button
                onClick={handleDiscard}
                disabled={saving}
                className="flex-1 rounded-xl border border-white/15 px-5 py-3 text-sm font-semibold text-slate-300 transition-colors hover:border-white/30 hover:text-white disabled:opacity-50 sm:flex-none"
              >
                Discard
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 px-8 py-3 text-sm font-bold text-white shadow-xl shadow-blue-900/30 transition-all hover:shadow-purple-900/40 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                  </>
                ) : (
                  'Save changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
